/**
 * Правка блока версии (цены, ФОТ, параметры): значения формы строками,
 * проверка ввода, сохранение с `expected_updated_at`, перечитывание после 409
 * и сводка «что пересчиталось». Тот же порядок, что у формы модели
 * справочника техники: свежая версия — основа, правки человека — поверх.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  apiErrorStatus,
  apiErrorText,
  prcVersionArg,
  useLazyGetPrcVersionQuery,
  type PrcVersion,
  type PrcVersionPayload,
} from '@shared/api';
import { NUMBER_HINT, normalizeDecimal } from '@entities/fleet';
import {
  isEditableStatus,
  readCells,
  rebaseValues,
  recalcSummary,
  writeCells,
  type BlockCell,
  type RecalcSummary,
} from '@entities/prc';

export interface BlockEditorConfig<T, L extends { cells: readonly BlockCell[] }> {
  payload: PrcVersionPayload;
  canWrite: boolean;
  /** Блок из версии. Функция — на уровне модуля: от неё зависит раскладка. */
  pick: (v: PrcVersion) => T;
  /** Раскладка ячеек по версии — тоже на уровне модуля. */
  layout: (v: PrcVersion) => L;
  save: (args: { id: string; block: T; expected_updated_at: string }) => Promise<PrcVersionPayload>;
  onDirtyChange: (count: number) => void;
  /** Префикс id полей. */
  idPrefix: string;
}

interface Form {
  base: PrcVersion;
  initial: Record<string, string>;
  values: Record<string, string>;
}

const REQUIRED_HINT = 'Укажите число';

export interface CellBinding {
  id: string;
  value: string;
  'aria-invalid': true | undefined;
  'aria-describedby': string | undefined;
  'data-changed': 'true' | undefined;
  'data-conflict': 'true' | undefined;
  onChange: (e: { target: { value: string } }) => void;
  onBlur: () => void;
}

export function useBlockEditor<T, L extends { cells: readonly BlockCell[] }>(
  cfg: BlockEditorConfig<T, L>,
) {
  const { payload, canWrite, pick, layout: makeLayout, save, onDirtyChange, idPrefix } = cfg;

  const formOf = useCallback(
    (v: PrcVersion): Form => {
      const init = readCells(pick(v), makeLayout(v).cells);
      return { base: v, initial: init, values: init };
    },
    [pick, makeLayout],
  );

  const [form, setForm] = useState<Form>(() => formOf(payload.version));
  const layout = useMemo(() => makeLayout(form.base), [form.base, makeLayout]);
  const cellIndex = useMemo(
    () => new Map(layout.cells.map((c, i) => [c.key, { cell: c, index: i }])),
    [layout],
  );
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ message: string; fresh: PrcVersion } | null>(null);
  const [conflictValues, setConflictValues] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );
  const [carried, setCarried] = useState<{
    carried: number;
    conflicts: number;
    dropped: number;
  } | null>(null);
  const [recalc, setRecalc] = useState<RecalcSummary | null>(null);
  const [fetchVersion, { isFetching: rereading }] = useLazyGetPrcVersionQuery();
  const rereadRef = useRef<HTMLButtonElement>(null);

  const changed = useMemo(() => {
    const keys = new Set([...Object.keys(form.values), ...Object.keys(form.initial)]);
    return new Set(
      Array.from(keys).filter((k) => (form.values[k] ?? '') !== (form.initial[k] ?? '')),
    );
  }, [form]);

  /** Проверка правленых ячеек: правки для блока и ошибки ввода. */
  const collected = useMemo(() => {
    const edits: Record<string, string | null> = {};
    const issues = new Map<string, string>();
    for (const key of changed) {
      const entry = cellIndex.get(key);
      if (!entry) continue;
      const { cell } = entry;
      const raw = (form.values[key] ?? '').trim();
      if (cell.kind === 'text' || cell.kind === 'flag') {
        edits[key] = raw;
        continue;
      }
      if (raw === '') {
        if (cell.optional) edits[key] = null;
        else issues.set(key, REQUIRED_HINT);
        continue;
      }
      const n = normalizeDecimal(raw);
      if (n === null) issues.set(key, NUMBER_HINT);
      else edits[key] = n;
    }
    return { edits, issues };
  }, [changed, cellIndex, form.values]);

  const editable = canWrite && isEditableStatus(form.base.status);
  const dirtyCount = changed.size;

  useEffect(() => {
    onDirtyChange(dirtyCount);
  }, [dirtyCount, onDirtyChange]);
  useEffect(() => () => onDirtyChange(0), [onDirtyChange]);

  // Версию обновили в другом месте (шапка, другая вкладка), а правок нет —
  // берём свежую. С правками основа не подменяется: её меняет «Перечитать».
  useEffect(() => {
    if (payload.version.updated_at === form.base.updated_at) return;
    if (dirtyCount > 0 || conflict) return;
    setForm(formOf(payload.version));
  }, [payload.version, form.base.updated_at, dirtyCount, conflict, formOf]);

  // Отказ сервера — плашку видно, при конфликте фокус на «Перечитать».
  useEffect(() => {
    if (conflict) rereadRef.current?.focus({ preventScroll: false });
  }, [conflict]);

  const domId = useCallback(
    (key: string) => `${idPrefix}-c${cellIndex.get(key)?.index ?? 'x'}`,
    [idPrefix, cellIndex],
  );

  const error = useCallback(
    (key: string) => ((showAll || touched.has(key)) && collected.issues.get(key)) || null,
    [showAll, touched, collected],
  );

  const bind = useCallback(
    (key: string): CellBinding => {
      const err = error(key);
      return {
        id: domId(key),
        value: form.values[key] ?? '',
        'aria-invalid': err ? true : undefined,
        'aria-describedby': err ? `${domId(key)}-error` : undefined,
        'data-changed': changed.has(key) ? 'true' : undefined,
        'data-conflict': conflictValues.has(key) ? 'true' : undefined,
        onChange: (e) => {
          const value = e.target.value;
          setForm((f) => ({ ...f, values: { ...f.values, [key]: value } }));
        },
        onBlur: () =>
          setTouched((prev) => {
            if (prev.has(key)) return prev;
            const next = new Set(prev);
            next.add(key);
            return next;
          }),
      };
    },
    [error, domId, form.values, changed, conflictValues],
  );

  const setValue = useCallback((key: string, value: string) => {
    setForm((f) => ({ ...f, values: { ...f.values, [key]: value } }));
  }, []);

  function focusFirstIssue() {
    const first = layout.cells.find((c) => collected.issues.has(c.key));
    if (!first) return;
    const el = document.getElementById(domId(first.key));
    el?.scrollIntoView({ block: 'center' });
    el?.focus({ preventScroll: true });
  }

  async function submit(): Promise<void> {
    setServerError(null);
    setRecalc(null);
    if (collected.issues.size > 0) {
      setShowAll(true);
      focusFirstIssue();
      return;
    }
    if (dirtyCount === 0) return;
    const block = writeCells(pick(form.base), layout.cells, collected.edits);
    const before = payload.models;
    setSaving(true);
    try {
      const res = await save({
        id: form.base.id,
        block,
        expected_updated_at: form.base.updated_at,
      });
      setForm(formOf(res.version));
      setRecalc(recalcSummary(before, res.models));
      setConflictValues(new Map());
      setCarried(null);
      setShowAll(false);
      setTouched(new Set());
    } catch (err) {
      if (apiErrorStatus(err) === 409) {
        // 409 — и «версию изменили», и «версия уже не черновик»: различаем
        // по свежей версии.
        const fresh = await fetchVersion(
          prcVersionArg(form.base.id, undefined, payload.variant?.id),
        )
          .unwrap()
          .catch(() => null);
        if (
          fresh &&
          isEditableStatus(fresh.version.status) &&
          fresh.version.updated_at !== form.base.updated_at
        ) {
          setConflict({
            message: apiErrorText(err, 'Версию уже изменил кто-то другой.'),
            fresh: fresh.version,
          });
          return;
        }
      }
      setServerError(apiErrorText(err, 'Не удалось сохранить и пересчитать.'));
    } finally {
      setSaving(false);
    }
  }

  function reread() {
    if (!conflict) return;
    const fresh = conflict.fresh;
    const freshInitial = readCells(pick(fresh), makeLayout(fresh).cells);
    const r = rebaseValues(form.values, form.initial, freshInitial);
    setForm({ base: fresh, initial: r.initial, values: r.values });
    setConflictValues(new Map(r.conflicts.map((k) => [k, r.initial[k] ?? ''])));
    const carriedCount = Object.keys(r.values).filter(
      (k) => (r.values[k] ?? '') !== (r.initial[k] ?? ''),
    ).length;
    setCarried({ carried: carriedCount, conflicts: r.conflicts.length, dropped: r.dropped.length });
    setConflict(null);
  }

  function reset() {
    setForm((f) => ({ ...f, values: f.initial }));
    setConflictValues(new Map());
    setCarried(null);
    setShowAll(false);
    setTouched(new Set());
    setServerError(null);
  }

  return {
    base: form.base,
    layout,
    editable,
    values: form.values,
    initial: form.initial,
    bind,
    setValue,
    error,
    domId,
    changed,
    dirtyCount,
    issues: collected.issues,
    showIssues: showAll && collected.issues.size > 0,
    focusFirstIssue,
    conflictValues,
    conflict,
    carried,
    recalc,
    dismissRecalc: () => setRecalc(null),
    saving,
    rereading,
    serverError,
    submit,
    reset,
    reread,
    rereadRef,
  };
}

export type BlockEditor = ReturnType<typeof useBlockEditor>;
